import React from 'react';
import { motion } from 'framer-motion';
import { FaRocket, FaSatellite, FaUserAstronaut } from 'react-icons/fa';
import SEO from '../SEO';
import { buildStructuredData, getAbsoluteUrl, getKeywords, getRouteSeo, siteSeo } from '../../seo/seoUtils';
import './About.css';

const capabilities = [
  {
    icon: FaRocket,
    title: 'Launch strategy',
    description: 'Mission planning built for small payloads, tight timelines, and real-world launch economics.'
  },
  {
    icon: FaSatellite,
    title: 'Satellite systems',
    description: 'CubeSat and spacecraft development focused on reliability, integration quality, and fast iteration.'
  },
  {
    icon: FaUserAstronaut,
    title: 'Program execution',
    description: 'Cross-functional teams that move quickly without losing the process discipline aerospace demands.'
  }
];

const metrics = [
  { value: '2018', label: 'Year founded' },
  { value: '2', label: 'Launch vehicles in design' },
  { value: '6', label: 'Satellite programs planned' },
  { value: '2028+', label: 'First flight target' }
];

const principles = [
  'Build with launch constraints in mind from day one.',
  'Keep hardware, software, and operations aligned.',
  'Design systems that scale from prototype to orbit.'
];

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.05 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const About = ({ enableSEO = true }) => {
  const seo = getRouteSeo('/about');
  const HeadingTag = enableSEO ? 'h1' : 'h2';

  return (
    <>
      {enableSEO && (
        <SEO
          title={seo.title}
          description={seo.description}
          keywords={getKeywords(seo)}
          url={getAbsoluteUrl('/about')}
          image={siteSeo.image}
          imageAlt={siteSeo.imageAlt}
          structuredData={buildStructuredData('/about')}
        />
      )}

      <section id="about" className="about-section">
        <div className="about-section__container">
          <motion.div
            className="about-section__intro"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.div className="about-section__copy" variants={fadeUp}>
              <span className="section-kicker">About Antaral</span>
              <HeadingTag className="section-heading">
                Space systems designed with <span>startup speed</span> and mission discipline.
              </HeadingTag>
              <p className="section-copy">
                We are building a modern aerospace company around practical launch support, dependable
                satellite systems, and engineering workflows that help ambitious teams get to orbit with
                less friction — still early, still designing, and clear about where we are.
              </p>
            </motion.div>

            <motion.div className="about-section__panel glass-card" variants={fadeUp}>
              <span className="about-section__panel-label">Why teams choose us</span>
              <ul className="about-section__principles">
                {principles.map((principle) => (
                  <li key={principle}>{principle}</li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div
            className="about-section__capabilities"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {capabilities.map((capability) => {
              const IconComponent = capability.icon;
              return (
                <motion.article
                  key={capability.title}
                  className="about-section__card glass-card"
                  variants={cardVariants}
                >
                  <div className="about-section__icon">
                    <IconComponent aria-hidden="true" />
                  </div>
                  <h3>{capability.title}</h3>
                  <p>{capability.description}</p>
                </motion.article>
              );
            })}
          </motion.div>

          <motion.div
            className="about-section__metrics"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {metrics.map((metric) => (
              <motion.div
                key={metric.label}
                className="about-section__metric glass-card"
                variants={cardVariants}
              >
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
